import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Input } from '@/components/ui/input';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';

import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { practitionerStaffService } from '@/services/practitionerStaffService';
import { toast } from '@/hooks/use-toast';
interface CreatePractitionerStaffRequest {
  first_name: string
  last_name: string
  email: string
  phone_number: string
  clinic_id: string | number
}

import PhoneInput from '../ui/phone-input';

import { isValidPhoneNumber } from 'react-phone-number-input';

const practitionerStaffSchema = z.object({
    first_name: z
        .string()
        .trim()
        .min(2, { message: 'First name must be at least 2 characters' })
        .max(50, { message: 'First name must be at most 50 characters' }),
    last_name: z
        .string()
        .trim()
        .max(50, { message: 'Last name must be at most 50 characters' })
        .optional()
        .or(z.literal('')),
    email: z
        .string()
        .trim()
        .min(1, { message: 'Email is required' })
        .email({ message: 'Please enter a valid email address' }),
    phone_number: z
        .string()
        .optional()
        .refine(
            val => {
                if (!val || val.trim() === '') return true; // allow empty
                return !!isValidPhoneNumber(val);
            },
            { message: 'Please enter a valid phone number' }
        ),
    clinic_id: z
        .string()
        .trim()
        .min(1, { message: 'Clinic is required' }),
});

type PractitionerStaffFormData = z.infer<typeof practitionerStaffSchema>;

interface AddPractitionerStaffSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: (practitioner: any) => void;
    clinicId: string;
}

const AddPractitionerStaffSheet = ({
    open,
    onOpenChange,
    onSuccess,
    clinicId,
}: AddPractitionerStaffSheetProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const form = useForm<PractitionerStaffFormData>({
        resolver: zodResolver(practitionerStaffSchema),
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            clinic_id: clinicId,
        },
    });

    const handleSubmit = async (practitionerData: PractitionerStaffFormData) => {
        try {
            setIsSubmitting(true);

            // Create new practitioner
            const apiData: CreatePractitionerStaffRequest = {
                first_name: practitionerData.first_name.trim(),
                last_name: practitionerData.last_name.trim(),
                email: practitionerData.email,
                phone_number: practitionerData.phone_number,
                clinic_id: clinicId,
            };

            const createdPractitioner = await practitionerStaffService.createClinicPractitionerStaff(clinicId, apiData);

            toast({
                title: 'Practitioner Staff Added',
                description:
                    'The practitioner staff is being provisioned. This may take 10–15 minutes.',
                variant: 'success',
            });

            // Call the callback to refresh the parent component
            onSuccess(createdPractitioner);

            form.reset();
            onOpenChange(false);
        } catch (error: any) {
            console.error('Error saving practitioner staff:', error);

            toast({
                title: 'Error',
                description: error.message || `Failed to create practitioner staff. Please try again.`,
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-full !max-w-[640px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                    <h2 className="text-base font-semibold text-slate-900">
                        Add Practitioner Staff
                    </h2>
                    <button
                        onClick={() => onOpenChange(false)}
                        className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                    >
                        <X className="h-5 w-5 text-slate-600" />
                    </button>
                </div>

                {/* Content */}
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col flex-1 overflow-hidden">
                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            <div className="w-full space-y-6">
                                {/* Practitioner Staff Details Section */}
                                <div className="space-y-4">
                                    <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">
                                        Practitioner Staff Details
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="first_name"
                                            render={({ field }) => (
                                                <FormItem className="space-y-1.5">
                                                    <FormLabel className="text-sm font-medium text-slate-700 required">
                                                        First Name
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            placeholder="Please enter first name"
                                                            {...field}
                                                            onChange={e => {
                                                                const value = e.target.value || '';
                                                                field.onChange(value);
                                                            }}
                                                            className="h-10"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="last_name"
                                            render={({ field }) => (
                                                <FormItem className="space-y-1.5">
                                                    <FormLabel className="text-sm font-medium text-slate-700">
                                                        Last Name
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            placeholder="Please enter last name"
                                                            {...field}
                                                            onChange={e => {
                                                                const value = e.target.value || '';
                                                                field.onChange(value);
                                                            }}
                                                            className="h-10"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="email"
                                            render={({ field }) => (
                                                <FormItem className="space-y-1.5">
                                                    <FormLabel className="text-sm font-medium text-slate-700 required">
                                                        Email
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="text"
                                                            placeholder="Please enter email"
                                                            {...field}
                                                            onChange={e => {
                                                                const value = e.target.value || '';
                                                                field.onChange(value.trim());
                                                            }}
                                                            className="h-10"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <PhoneInput
                                            label="Phone"
                                            name="phone_number"
                                            form={form}
                                            placeholder="Please enter phone number"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                disabled={isSubmitting}
                                className="text-slate-600 hover:text-slate-900"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="bg-brand-teal hover:bg-brand-teal/90 text-white"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    'Add Practitioner Staff'
                                )}
                            </Button>
                        </div>
                    </form>
                </Form>
            </SheetContent>
        </Sheet>
    );
};

export default AddPractitionerStaffSheet;
